import { useState, useContext, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { GridRowSelectionModel } from '@mui/x-data-grid'
import {
  PaginationResponse,
  OrderByDirection,
  FetchUsersRequest,
} from '../swagger'
import { userAccountsApi, teams, extractedErrorObject } from '../api/swagger'
import {
  SnackbarSeverity,
  useSnackbarContext,
} from '../components/Context/SnackbarContext'
import { LoadingContext } from '../components/Context/LoadingContext'

interface PotentialSupervisor {
  firstName: string
  lastName: string
  roleName: string
  actorKey: number
}

interface UseTeamReassignmentProps {
  onReassignComplete?: (pagination: PaginationResponse) => void
  currentPage: number
  pageSize: number
  totalCount: number
}

interface UseTeamReassignmentReturn {
  selectedRows: string[]
  isReassignModalOpen: boolean
  selectedSupervisor: PotentialSupervisor | null
  isReassigning: boolean
  supervisorOptions: PotentialSupervisor[]
  isLoadingSupervisors: boolean
  supervisorSearchInput: string
  handleSelectionChange: (selectedRowIds: GridRowSelectionModel) => void
  handleOpenReassignModal: () => void
  handleCloseReassignModal: () => void
  handleReassignSupervisor: () => Promise<void>
  setSelectedSupervisor: (supervisor: PotentialSupervisor | null) => void
  setSupervisorSearchInput: (input: string) => void
}

export const useTeamReassignment = ({
  onReassignComplete,
  currentPage,
  pageSize,
  totalCount,
}: UseTeamReassignmentProps): UseTeamReassignmentReturn => {
  const { t } = useTranslation()
  const { addLoadingIds, removeLoadingIds } = useContext(LoadingContext)
  const { setSnackbarMessage, setSnackbarSeverity, setSnackbarState } =
    useSnackbarContext()

  // State
  const [selectedRows, setSelectedRows] = useState<string[]>([])
  const [isReassignModalOpen, setIsReassignModalOpen] = useState(false)
  const [selectedSupervisor, setSelectedSupervisor] =
    useState<PotentialSupervisor | null>(null)
  const [isReassigning, setIsReassigning] = useState(false)
  const [supervisorOptions, setSupervisorOptions] = useState<
    PotentialSupervisor[]
  >([])
  const [isLoadingSupervisors, setIsLoadingSupervisors] = useState(false)
  const [supervisorSearchInput, setSupervisorSearchInput] = useState('')

  const reassignSupervisorLoadingId = 'TeamReassignment-reassignSupervisor'

  const fetchSupervisors = useCallback(
    async (search?: string) => {
      setIsLoadingSupervisors(true)
      try {
        const params: FetchUsersRequest = {
          downlineOnly: true,
          validNowOnly: true,
          orderBy: ['lastName ASC'],
        }

        // Only add search param if it's provided and has at least 3 characters
        if (search && search.length >= 3) {
          params.search = search

          const { userAccounts } = await userAccountsApi.fetchUsers(params)

          const validSupervisors = userAccounts
            .filter((user) => user.roles.length > 0)
            .flatMap((userAccount) => {
              return userAccount.roles.map((role) => {
                return {
                  firstName: userAccount.firstName,
                  lastName: userAccount.lastName,
                  roleName: role.name,
                  actorKey: role.actorKey,
                }
              })
            }) as PotentialSupervisor[]

          // Only update options if we have results or if we're clearing the search
          if (validSupervisors.length > 0 || !search || search === '') {
            setSupervisorOptions(validSupervisors)
          }
        }
      } catch (e) {
        const errorObject = (await extractedErrorObject(e)) ?? {
          code: 'UnknownError',
          message:
            (e as unknown as Error).message ??
            t(
              'Team.Reassign.Error.FetchingSupervisors',
              'An error occurred while fetching potential support.'
            ),
        }

        setSnackbarMessage(errorObject.message)
        setSnackbarSeverity(SnackbarSeverity.Error)
        setSnackbarState(true)
      } finally {
        setIsLoadingSupervisors(false)
      }
    },
    [setSnackbarMessage, setSnackbarSeverity, setSnackbarState, t]
  )

  const handleSelectionChange = (selectedRowIds: GridRowSelectionModel) => {
    const stringRowIds = selectedRowIds.map((id) => id.toString())
    setSelectedRows(stringRowIds)
  }

  const handleOpenReassignModal = useCallback(() => {
    if (selectedRows.length === 0) {
      setSnackbarMessage(
        t(
          'Team.Reassign.NoSelection',
          'Please select at least one team member to reassign.'
        )
      )
      setSnackbarSeverity(SnackbarSeverity.Warning)
      setSnackbarState(true)
      return
    }
    setIsReassignModalOpen(true)
  }, [
    selectedRows,
    setSnackbarMessage,
    setSnackbarSeverity,
    setSnackbarState,
    t,
  ])

  const handleCloseReassignModal = useCallback(() => {
    setIsReassignModalOpen(false)
    setSelectedSupervisor(null)
    setSupervisorSearchInput('')
  }, [])

  const handleReassignSupervisor = useCallback(async () => {
    if (!selectedSupervisor) {
      setSnackbarMessage(
        t(
          'Team.Reassign.NoSupervisor',
          'Please select a support to reassign to.'
        )
      )
      setSnackbarSeverity(SnackbarSeverity.Warning)
      setSnackbarState(true)
      return
    }

    setIsReassigning(true)
    addLoadingIds([reassignSupervisorLoadingId])

    try {
      const newSupervisorActorKey = selectedSupervisor.actorKey

      if (!newSupervisorActorKey) {
        throw new Error(
          t(
            'Team.Reassign.Error.NoSupervisorRole',
            'The selected support does not have a valid role.'
          )
        )
      }

      const actorKeysToReassign = selectedRows
        .map((rowId) => Number(rowId))
        .filter((actorKey) => !isNaN(actorKey))

      await teams.bulkReassignTeamMembers({
        body: {
          newSupervisorActorKey,
          actorKeysToReassign,
        },
      })

      setSnackbarMessage(
        t(
          'Team.Reassign.Success',
          'Team members successfully reassigned to new support.'
        )
      )
      setSnackbarSeverity(SnackbarSeverity.Success)
      setSnackbarState(true)

      handleCloseReassignModal()
      setSelectedRows([])

      if (onReassignComplete) {
        onReassignComplete({
          page: currentPage,
          pageSize: pageSize,
          totalCount: totalCount,
          orderBy: [{ date: OrderByDirection.Asc }],
        })
      }
    } catch (e) {
      const errorObject = (await extractedErrorObject(e)) ?? {
        code: 'UnknownError',
        message:
          (e as unknown as Error).message ??
          t(
            'Team.Reassign.Error',
            'An error occurred while reassigning team members.'
          ),
      }

      setSnackbarMessage(errorObject.message)
      setSnackbarSeverity(SnackbarSeverity.Error)
      setSnackbarState(true)
    } finally {
      setIsReassigning(false)
      removeLoadingIds([reassignSupervisorLoadingId])
    }
  }, [
    selectedSupervisor,
    selectedRows,
    addLoadingIds,
    removeLoadingIds,
    handleCloseReassignModal,
    reassignSupervisorLoadingId,
    t,
    setSnackbarMessage,
    setSnackbarSeverity,
    setSnackbarState,
    onReassignComplete,
    currentPage,
    pageSize,
    totalCount,
  ])

  // Debounce supervisor search input
  useEffect(() => {
    if (supervisorSearchInput.length >= 3) {
      // If input has enough characters, search with the input
      fetchSupervisors(supervisorSearchInput)
    } else {
      // If input is cleared, fetch default options
      fetchSupervisors()
    }
  }, [supervisorSearchInput, fetchSupervisors])

  return {
    selectedRows,
    isReassignModalOpen,
    selectedSupervisor,
    isReassigning,
    supervisorOptions,
    isLoadingSupervisors,
    supervisorSearchInput,
    handleSelectionChange,
    handleOpenReassignModal,
    handleCloseReassignModal,
    handleReassignSupervisor,
    setSelectedSupervisor,
    setSupervisorSearchInput,
  }
}

export default useTeamReassignment
